import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicHeader, ArabicFooter } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const fetchJson = async (endpoint, signal) => {
  const headers = { Accept: "application/json" };
  const token = getJwtToken();
  if (token) headers.Authorization = "Bearer " + token;

  const response = await fetch(getApiUrl(endpoint), {
    method: "GET",
    headers,
    credentials: "include",
    signal,
  });

  if (!response.ok) throw new Error(endpoint + " failed with status " + response.status);
  return response.json();
};

const mapBrief = (item, language = "en") => {
  if (!item) return { stats: [], infoCards: [] };
  const text = (field) => language === "ar" ? item[`${field}_ar`] || item[field] || item[`${field}_en`] || "" : item[`${field}_en`] || item[field] || item[`${field}_ar`] || "";
  return {
    title: text("title"), subtitle: text("subtitle"), description: stripHtml(text("description")),
    buttonText: text("btncaption"), buttonUrl: item.btnurl || "", rightBoxTitle: text("rightboxtitle"), rightBoxDescription: stripHtml(text("rightboxdescription")),
    stats: [
      { icon: "fa-solid fa-calendar-check", title: text("Establishedtitle"), subtitle: text("Establishedsubtitle") },
      { icon: "fa-solid fa-file-signature", title: text("permittitle"), subtitle: text("permitsubtitle") },
      { icon: "fa-solid fa-location-dot", title: text("approvedtitle"), subtitle: text("approvedsubtitle") },
      { icon: "fa-solid fa-hands-holding-child", title: text("focustitle"), subtitle: text("focussubtitle") },
    ].filter((stat) => stat.title || stat.subtitle),
    briefTitle: text("midtitle1"), briefSmallTitle: text("midtitle2"), briefCardTitle: text("midtitle3"), briefCardDescription: stripHtml(text("middiscription")),
    image: getCloudflareImageUrl(item.ImagePath, "w=900,sharpen=1"),
    infoCards: [
      { image: getCloudflareImageUrl(item.box1icon, "w=120,sharpen=1"), title: text("box1title"), description: stripHtml(text("box1description")) },
      { image: getCloudflareImageUrl(item.box2icon, "w=120,sharpen=1"), title: text("box2title"), description: stripHtml(text("box2description")) },
    ].filter((card) => card.title || card.description || card.image),
    messageTitle: text("ourmessagetitle"), messageDescription: stripHtml(text("ourmessagedescription")),
    bottomTitle: text("bottomtitle"), bottomDescription: stripHtml(text("bottomdescription")), bottomButtonText: text("bottombtncaption"), bottomButtonUrl: item.bottombtnurl || "",
  };
};

const localizedApiValue = (item, field, language, fallback = "") => language === "ar"
  ? item?.[`${field}_ar`] || item?.[field] || item?.[`${field}_en`] || fallback
  : item?.[`${field}_en`] || item?.[field] || item?.[`${field}_ar`] || fallback;

const mapPrograms = (programs, programItems, language = "en") => programs.slice(0, 2).map((program, index) => ({
  id: program.id || String(index + 1), order: index === 0 ? "First" : "Second",
  title: localizedApiValue(program, "title", language), subtitle: localizedApiValue(program, "subtitle", language),
  image: getCloudflareImageUrl(program.box1icon, "w=120,sharpen=1"),
  items: programItems.filter((item) => String(item.catid) === String(program.id)).map((item) => localizedApiValue(item, "title", language)).filter(Boolean),
}));

const mapCategories = (categories, subCategories, language = "en") => categories.slice(0, 2).map((category, index) => ({
  id: category.id || String(index + 1), smallTitle: localizedApiValue(category, "title", language), title: localizedApiValue(category, "subtitle", language),
  items: subCategories.filter((item) => String(item.catid) === String(category.id)).map((item) => ({
    id: item.id, title: localizedApiValue(item, "title", language), description: stripHtml(localizedApiValue(item, "description", language)),
  })).filter((item) => item.title),
}));

export default function About({ language = "en" }) {
  const interactions = useHomeInteractions();
  const [brief, setBrief] = useState({ stats: [], infoCards: [] });
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    document.title = "About Tarahum Foundation";
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadAboutContent = async () => {
      try {
        const [
          briefResult,
          programResult,
          programItemResult,
          categoryResult,
          subCategoryResult,
        ] = await Promise.all([
          fetchJson("/api/GetFoundationBriefMainList.ashx", controller.signal),
          fetchJson("/api/GetFoundationProgramList.ashx?page=1&pageSize=20", controller.signal),
          fetchJson("/api/GetFoundationProgramListList.ashx?page=1&pageSize=200", controller.signal),
          fetchJson("/api/GetFoundationCategoryList.ashx", controller.signal),
          fetchJson("/api/GetFoundationSubCategoryList.ashx", controller.signal),
        ]);

        const apiBrief = Array.isArray(briefResult?.data) && briefResult.data.length ? mapBrief(briefResult.data[0], language) : { stats: [], infoCards: [] };
        const apiPrograms = mapPrograms(
          Array.isArray(programResult?.data) ? programResult.data : [],
          Array.isArray(programItemResult?.data) ? programItemResult.data : [],
          language
        );
        const apiCategories = mapCategories(
          Array.isArray(categoryResult?.data) ? categoryResult.data : [],
          Array.isArray(subCategoryResult?.data) ? subCategoryResult.data : [],
          language
        );

        setBrief(apiBrief);
        setPrograms(apiPrograms);
        setCategories(apiCategories);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load about page API content", error);
        }
      }
    };

    loadAboutContent();
    return () => controller.abort();
  }, [language]);

  const donationLink = useMemo(() => (
    brief.buttonUrl ? (brief.buttonUrl.startsWith("/") ? brief.buttonUrl : "/" + brief.buttonUrl.replace(/^\/+/, "")) : "#"
  ), [brief.buttonUrl]);

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    const prefixArabicLinks = () => document.querySelectorAll("a[href^='/']").forEach((link) => {
      const href = link.getAttribute("href");
      if (!href || href === "/" || link.hasAttribute("data-language-switch")) return;
      const withoutLanguageQuery = href.replace(/[?&]lang=ar(?=&|$)/i, "").replace(/[?&]$/, "");
      link.setAttribute("href", withoutLanguageQuery === "/ar" || withoutLanguageQuery.startsWith("/ar/") ? withoutLanguageQuery : `/ar${withoutLanguageQuery}`);
    });
    prefixArabicLinks();
    const linkObserver = new MutationObserver(prefixArabicLinks);
    linkObserver.observe(document.body, { childList: true, subtree: true });
    return () => {
      linkObserver.disconnect();
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [language]);

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="abt-re-new">
        <div className="abt-container">
          <div className="abt-hero">
            <div className="abt-hero-content">
              <div className="abt-badge">
                <i className="fa-solid fa-hand-holding-heart"></i>
                {brief.subtitle}
              </div>
              <h1>{brief.title}</h1>
              <p>{brief.description}</p>
              <div className="abt-hero-actions">
                <Link to={donationLink} className="abt-btn abt-btn-primary">
                  <i className="fa-solid fa-heart"></i>
                  {brief.buttonText}
                </Link>
              </div>
            </div>

            <div className="abt-hero-card">
              <div className="abt-hero-card-icon">
                <i className="fa-solid fa-people-carry-box"></i>
              </div>
              <h3>{brief.rightBoxTitle}</h3>
              <p>{brief.rightBoxDescription}</p>
            </div>
          </div>
        </div>

        <section>
          <div className="container">
            <div className="abt-stats">
              {brief.stats.map((stat) => (
                <div className="abt-stat" key={stat.title}>
                  <i className={stat.icon}></i>
                  <div>
                    <span>{stat.title}</span>
                    <strong>{stat.subtitle}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="container">
            <div className="abt-section">
              <div className="abt-section-head">
                <div>
                  <div className="abt-title-small">{brief.briefSmallTitle}</div>
                  <h2>{brief.briefTitle}</h2>
                </div>
              </div>

              <div className="abt-brief-card">
                <div className="abt-brief-img" style={{ backgroundImage: `url(${brief.image})` }}></div>
                <div className="abt-brief-content">
                  <h3>{brief.briefCardTitle}</h3>
                  <p>{brief.briefCardDescription}</p>
                  <div className="abt-info-grid">
                    {brief.infoCards.map((card) => (
                      <div className="abt-info-card" key={card.title}>
                        <div className="abt-info-icon">
                          {card.image ? <img src={card.image} alt="" /> : <i className={card.icon}></i>}
                        </div>
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="abt-section">
              <div className="abt-message">
                <i className="fa-solid fa-quote-left"></i>
                <div>
                  <h2>{brief.messageTitle}</h2>
                  <p>{brief.messageDescription}</p>
                </div>
              </div>
            </div>

            <div className="abt-section">
              <div className="abt-section-head">
                <div>
                  <div className="abt-title-small">{language === "ar" ? "الأنشطة الخيرية" : "Charitable Activities"}</div>
                  <h2>{language === "ar" ? "برامج تُحدث أثرًا حقيقيًا" : "Programs that create real impact"}</h2>
                </div>
                <p>{language === "ar" ? "تقدم المؤسسة المساعدات المالية والعينية، وحفظ النعمة، وكفالة الأيتام، والمساعدات الإنسانية الطارئة." : "The foundation provides financial aid, in-kind assistance, food preservation, orphan sponsorship, and urgent emergency aid."}</p>
              </div>

              <div className="abt-activity-grid">
                {programs.map((program) => (
                  <div className="abt-activity-card" key={program.id}>
                    <div className="abt-card-top">
                      <div className="abt-card-icon">
                        {program.image ? <img src={program.image} alt="" /> : <i className={program.icon}></i>}
                      </div>
                      <div> 
                        <h3>{program.title}</h3>
                      </div>
                    </div>
                    {program.subtitle ? <p>{program.subtitle}</p> : null}
                    <ul className="abt-list">
                      {program.items.map((item) => (
                        <li key={item}>
                          <i className="fa-solid fa-check"></i>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {categories.map((category) => (
              <div className="abt-section" key={category.id}>
                <div className="abt-section-head">
                  <div>
                    <div className="abt-title-small">{category.smallTitle}</div>
                    <h2>{category.title}</h2>
                  </div>
                </div>
                <div className="abt-project-grid">
                  {category.items.map((item, index) => (
                    <div className="abt-project-card" key={item.id || item.title}>
                      <div className="num">{String(index + 1).padStart(2, "0")}</div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-50">
          <div className="container">
            <div className="abt-cta">
              <div>
                <h2>{brief.bottomTitle}</h2>
                <p>{brief.bottomDescription}</p>
              </div>
              <Link to={brief.bottomButtonUrl || donationLink} className="abt-btn abt-btn-primary">
                <i className="fa-solid fa-hand-holding-heart"></i>
                {brief.bottomButtonText}
              </Link>
            </div>
          </div>
        </section>
      </section>

      {language === "ar" ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
